import {
  AbstractControl,
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { Injectable, OnDestroy } from '@angular/core';
import { AddressModel, BillNumberModel, CountryModel, ShipmentModel, ValidationMessageModel } from '@data-layer/models/common';
import { IsfV1EntityModel } from '@data-layer/models/isf-v1';
import { EntityModel } from '@data-layer/models/entities';
import {
  CustomValidator,
  emailPattern,
  hazardousGoodsClaimedValidator,
  identifierEINValidator,
  identifierSSNValidator,
  referenceNumberValidator,
  suretyCodeValidator,
} from './custom-validator';
import { DeliveryOrderModel } from '@data-layer/models/entry-summaries';
import {
  enumDeliveryOrderType,
  enumEntrySummaryDutiesPaymentType,
  enumEntrySummarySubmissionType,
  enumEntrySummaryType,
  enumTransportationMode,
} from '@data-layer/enums/entry-summaries';
import {
  enumBondType,
  enumIdentifierQualifier,
  enumLengthUnit,
  enumValidationMessageSeverity,
  enumWeightUnit,
} from '@data-layer/enums/common';
import { IErrorForm } from '@shared/form-wrapper/interfaces';
import { enumEntityModelCode } from '@data-layer/enums/entities';
import { MISSING_ADDRESS, MISSING_CITY, MISSING_COUNTRY, MISSING_POSTAL_CODE, MISSING_STATE } from '@constants/entity-validation-messages';
import { enumFormControlType } from '@enum/enumFormControlType';
import { IFieldTypeConfig } from '@interfaces/field-type-config';
import { ReplaySubject, Subscription, timer } from 'rxjs';
import { ManagementMasterBillModel, ManagementVoyageModel } from '@data-layer/models/management';
import {
  enumAggregationType,
  enumBillStatus,
  enumCostType,
  enumMarginType,
  enumPackageType,
  enumPublishingState,
} from '@data-layer/enums/lcl';
import {
  CANADA_COUNTRY_CODE,
  LA_TIMEZONE_NAME,
  LINE_ITEM_DESCRIPTION_MAX_LENGTH,
  MEXICO_COUNTRY_CODE,
  USA_COUNTRY_CODE,
} from '@constants/constants';
import { DateTimezoneService } from '@services/date-timezone.service';
import { distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { enumIsfShipmentCode, enumIsfShipmentType } from '@data-layer/enums/isf';
import { EntityValidationHelper } from '@helpers/entity-validation.helper';
import { enumShippingRole } from '@data-layer/enums/user';
import { cloneDeep, every, omit, pick } from 'lodash-es';
import { UtilsHelper } from '@helpers/utils-helper';
import { enumCompanyBusinessType } from '@app/data-layer/enums/companies';

@Injectable({ providedIn: 'root' })
export class FormHelper implements OnDestroy {
  private focusSubscription: Subscription;

  private destroy$: ReplaySubject<void> = new ReplaySubject<void>();
  /** Mirrors the `F{20}` mask on the Vessel name inputs (entry shipment forms). */
  private readonly vesselNameMaxLength: number = 20;

  constructor(private fb: UntypedFormBuilder, private dateTimezoneService: DateTimezoneService) {}

  public ngOnDestroy(): void {
    this.destroy$.next();
  }

  public createPackageManagementFormGroup() {
    return this.fb.group({
      LPN: null,
      dimensions: this.fb.group({
        height: [null, Validators.required],
        width: [null, Validators.required],
        length: [null, Validators.required],
      }),
      weightUnit: [null, Validators.required],
      lengthUnit: [null, Validators.required],
      weight: [null, Validators.required],
      actualWeight: null,
      volume: null,
      packingListItemId: null,
      marksAndNumbers: null,
      description: null,
      quantity: [null, Validators.required],
      type: [null, Validators.required],
      stackable: false,
      lotNumber: null,
      state: [null],
    });
  }

  public createQuoteFormGroup(hasNoHazardousGoodsClaimed: boolean = true) {
    return this.fb.group({
      incoTermsType: [null, [Validators.required]],
      shippingFrom: [null],
      shippingTo: [null],
      packages: this.fb.array([this.createPackageFormGroup()]),
      isfRequested: [null],
      entrySummaryRequested: [null],
      exportLicenseRequested: [null],
      marineInsuranceRequested: [null],
      voyageId: [null],
      shippingRole: [null],
      ...(hasNoHazardousGoodsClaimed && { noHazardousGoodsClaimed: [false, hazardousGoodsClaimedValidator] }),
    });
  }

  public createPackageFormGroup(lengthUnit = enumLengthUnit.CENTIMETER, weightUnit = enumWeightUnit.KG, isView: boolean = false) {
    const minVal: number = 1;

    return this.fb.group({
      id: [null],
      dimensions: this.fb.group({
        height: [null, [Validators.required, Validators.min(minVal)]],
        width: [null, [Validators.required, Validators.min(minVal)]],
        length: [null, [Validators.required, Validators.min(minVal)]],
      }),
      lengthUnit: [lengthUnit, Validators.required],
      weight: [null, [Validators.required, Validators.min(minVal)]],
      weightUnit: [weightUnit, Validators.required],
      quantity: [1, [Validators.required, Validators.min(1)]],
      type: [enumPackageType.BOX, Validators.required],
      stackable: [true, Validators.required],
      palletStandard: [],
      isView: [isView],
    });
  }

  public createCombinedSubCostFormGroup(): UntypedFormGroup {
    return this.fb.group({
      id: [null],
      name: [null, Validators.required],
      transitSegment: [null, Validators.required],
      aggregationType: [enumAggregationType.FLAT],
      minimumCost: [null, Validators.min(0)],
      maximumCost: [null, Validators.min(0)],
      pricingVariants: new UntypedFormArray([this.createCombinedPricingVariantsFormGroup()]),
      targetContainerType: [null, Validators.required],
      targetContainerTypeColoading: [null],
      targetContainerTypeGroupage: [null],
      targetContainerTypeBoth: [null],
    });
  }

  public createCombinedMarginFormGroup(): UntypedFormGroup {
    return this.fb.group({
      originHblMargin: this.createCombinedMarginFormGroupItem(),
      originWeightMeasurementMargin: this.createCombinedMarginFormGroupItem(),
      freightWeightMeasurementMargin: this.createCombinedMarginFormGroupItem(),
      destinationHblMargin: this.createCombinedMarginFormGroupItem(),
      destinationWeightMeasurementMargin: this.createCombinedMarginFormGroupItem(),
      containerSettings: new UntypedFormArray([]),
    });
  }

  public createCombinedMarginFormGroupItem(): UntypedFormGroup {
    return this.fb.group({
      amount: [0, [Validators.required, Validators.min(0)]],
      type: [enumMarginType.FIXED],
    });
  }

  public createCombinedPricingVariantsFormGroup(containerTypeId?: number, rate?: number, unit?: enumCostType): UntypedFormGroup {
    return this.fb.group({
      rate: [!!rate ? rate : 0, [Validators.required, Validators.min(0)]],
      quantity: [1],
      unit: [unit, Validators.required],
      unitSelection: [null],
      containerTypeId: [!!containerTypeId ? containerTypeId : null],
      isUnitReal: [false],
    });
  }

  public createCombinedMultiplyingFormGroupItem(containerTypeId: number, typicalGoodsVolume: number = 0): UntypedFormGroup {
    return this.fb.group({
      containerTypeId: [containerTypeId, Validators.required],
      typicalGoodsVolume: [typicalGoodsVolume, [Validators.required, Validators.min(0)]],
    });
  }

  public createSalesTaxesFormGroup() {
    return new UntypedFormGroup({
      taxes: new UntypedFormControl(null, Validators.required),
      threshold: new UntypedFormControl(null, Validators.required),
    });
  }

  public createRouteFormGroup(): UntypedFormGroup {
    return new UntypedFormGroup({
      displayName: new UntypedFormControl(null, Validators.required),
      departurePortId: new UntypedFormControl(null, Validators.required),
      departurePortDisplayName: new UntypedFormControl(null, Validators.required),
      arrivalPortId: new UntypedFormControl(null, Validators.required),
      arrivalPortDisplayName: new UntypedFormControl(null, Validators.required),
      publishingState: new UntypedFormControl(enumPublishingState.DRAFT, Validators.required),
    });
  }

  public createVoyageFormGroup() {
    const formGroup = new UntypedFormGroup({
      displayName: new UntypedFormControl(null, Validators.required),
      estimatedDepartureTime: new UntypedFormControl(null, Validators.required),
      estimatedArrivalTime: new UntypedFormControl(null, Validators.required),
      actualDepartureTime: new UntypedFormControl(null),
      actualArrivalTime: new UntypedFormControl(null),
      estimatedDuration: new UntypedFormControl(null, Validators.required),
      departureStorageId: new UntypedFormControl(null, Validators.required),
      arrivalStorageId: new UntypedFormControl(null, Validators.required),
      containerCutoff: new UntypedFormControl(null, Validators.required),
      documentsCutoff: new UntypedFormControl(null, Validators.required),
      warehouseCutoff: new UntypedFormControl(null, Validators.required),
      status: new UntypedFormControl(null, Validators.required),
      publishingState: new UntypedFormControl(null, Validators.required),
      validityDate: new UntypedFormControl(null),
    });

    formGroup
      .get('estimatedDepartureTime')
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((estimatedDepartureTime) => {
        if (!estimatedDepartureTime) {
          return;
        }

        const containerCutoff = this.dateTimezoneService.addDays(estimatedDepartureTime, -2);
        const documentsCutoff = this.dateTimezoneService.addDays(estimatedDepartureTime, -3);
        const warehouseCutoff = this.dateTimezoneService.addDays(estimatedDepartureTime, -8);

        formGroup.patchValue({ containerCutoff, documentsCutoff, warehouseCutoff });
      });
    return formGroup;
  }

  public createContainerFreightStorageFormGroup(): UntypedFormGroup {
    return new UntypedFormGroup({
      displayName: new UntypedFormControl(null, Validators.required),
      portId: new UntypedFormControl(null, Validators.required),
      openingHours: new UntypedFormControl(null, Validators.required),
      location: new UntypedFormControl(null),
      isActive: new UntypedFormControl(false),
      type: new UntypedFormControl(null),
      phoneNumber: new UntypedFormControl(null),
      faxNumber: new UntypedFormControl(null),
      email: new UntypedFormControl(null, Validators.pattern(emailPattern)),
      notes: new UntypedFormControl(null),
      address: this.createGenericManagementAddressFormGroup(),
    });
  }

  public createGenericManagementAddressFormGroup(): UntypedFormGroup {
    return this.fb.group({
      city: new UntypedFormControl(null, [Validators.required]),
      country: new UntypedFormControl(null, [Validators.required]),
      postalCode: new UntypedFormControl(null, [Validators.required, Validators.maxLength(9)]),
      state: new UntypedFormControl(null, [Validators.required]),
      line1: new UntypedFormControl(null, [Validators.required, Validators.maxLength(32)]),
      line2: new UntypedFormControl(null),
      line3: new UntypedFormControl(null),
    });
  }

  public createGenericAddressFormGroup(): UntypedFormGroup {
    return this.fb.group({
      city: new UntypedFormControl(null, [Validators.required]),
      country: new UntypedFormControl(null, [Validators.required]),
      postalCode: new UntypedFormControl(null, [Validators.required, Validators.maxLength(9)]),
      state: new UntypedFormControl(null, [Validators.required]),
      line1: new UntypedFormControl(null, [Validators.required, Validators.maxLength(32)]),
      line2: new UntypedFormControl(null),
      line3: new UntypedFormControl(null),
    });
  }

  public createContainerFreightStorageManagerFormGroup() {
    return new UntypedFormGroup({
      name: new UntypedFormControl(null, Validators.required),
      phone: new UntypedFormControl(null, [Validators.maxLength(15)]),
      email: new UntypedFormControl(null, [Validators.required, Validators.maxLength(70), Validators.pattern(emailPattern)]),
      fax: new UntypedFormControl(null, [Validators.maxLength(15)]),
      address: new UntypedFormControl(null, Validators.required),
    });
  }

  public createContainerSealFormGroup() {
    return new UntypedFormGroup({
      sealNumber: new UntypedFormControl(null, Validators.required),
      setDate: new UntypedFormControl(null, Validators.required),
      brokenDate: new UntypedFormControl(null),
      brokenBy: new UntypedFormControl(null),
      description: new UntypedFormControl(null),
    });
  }

  public createContainerFormGroup() {
    return new UntypedFormGroup({
      displayName: new UntypedFormControl(null, Validators.required),
      containerNumber: new UntypedFormControl(null),
      price: new UntypedFormControl(null),
      bruttoWeight: new UntypedFormControl(null),
      taraWeight: new UntypedFormControl(null),
      netWeight: new UntypedFormControl(null),
      shippingOrderRemarks: new UntypedFormControl(null),
      shippingCompany: new UntypedFormControl(null),
      insuranceValue: new UntypedFormControl(null),
      status: new UntypedFormControl(null, Validators.required),
      geolocation: this.createGeolocationFormGroup(),
      quoteIds: new UntypedFormControl([]),
    });
  }

  public createGeolocationFormGroup() {
    return new UntypedFormGroup({
      longitude: new UntypedFormControl(null),
      latitude: new UntypedFormControl(null),
    });
  }

  public createContainerTypeFormGroup() {
    return new UntypedFormGroup({
      displayName: new UntypedFormControl(null, Validators.required),
      doorHeight: new UntypedFormControl(null, Validators.required),
      doorWidth: new UntypedFormControl(null, Validators.required),
      tareWeight: new UntypedFormControl(null, Validators.required),
      maxCargoWeight: new UntypedFormControl(null, Validators.required),
      capacity: new UntypedFormControl(null, Validators.required),
      typicalGoodsVolume: new UntypedFormControl(null, Validators.required),
      internalDimension: this.createFreightDimensionsFormGroup(),
      externalDimension: this.createFreightDimensionsFormGroup(),
      isDefault: new UntypedFormControl(null),
    });
  }

  public createFreightDimensionsFormGroup() {
    return new UntypedFormGroup({
      height: new UntypedFormControl(null, Validators.required),
      width: new UntypedFormControl(null, Validators.required),
      length: new UntypedFormControl(null, Validators.required),
    });
  }

  public createMasterBillFormGroup() {
    const shipper = this.createLCLEntityModelFormGroup(enumEntityModelCode.SHIPPER);
    const consignee = this.createLCLEntityModelFormGroup(enumEntityModelCode.CONSIGNEE);
    const notifyParty = this.createLCLEntityModelFormGroup(enumEntityModelCode.NOTIFY_PARTY);
    const forwardingAgent = this.createLCLEntityModelFormGroup(enumEntityModelCode.FORWARDING_AGENT);
    const formGroup = new UntypedFormGroup({
      displayName: new UntypedFormControl(null, Validators.required),
      status: new UntypedFormControl(null, Validators.required),
      billNumber: new UntypedFormGroup({
        carrier: new UntypedFormControl(null, Validators.required),
        number: new UntypedFormControl(null, Validators.required),
      }),
      quoteReferenceNumber: new UntypedFormControl(null, Validators.required),
      contractNumber: new UntypedFormControl(null, Validators.required),
      remarks: new UntypedFormControl(null),
      shippedOnBoardDate: new UntypedFormControl(null, Validators.required),
      issueDate: new UntypedFormControl(null, Validators.required),
      placeOfIssue: new UntypedFormControl(null, Validators.required),
      freightPaymentPlace: new UntypedFormControl(null, Validators.required),
      freightPayableTotalAmount: new UntypedFormControl(null, Validators.required),
      receiptPlace: new UntypedFormControl(null, Validators.required),
      deliveryPlace: new UntypedFormControl(null, Validators.required),
      finalDestination: new UntypedFormControl(null, Validators.required),
      incoTermCode: new UntypedFormControl(null, Validators.required),
      shipper,
      consignee,
      notifyParty,
      forwardingAgent,
    });

    formGroup
      .get('billNumber')
      .valueChanges.pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe((billNumber: BillNumberModel) => {
        if (formGroup.get('status').value !== enumBillStatus.QUOTE && billNumber && (billNumber.carrier || billNumber.number)) {
          formGroup.get('displayName').patchValue((billNumber.carrier || '') + (billNumber.number || ''));
        }
      });
    formGroup
      .get('quoteReferenceNumber')
      .valueChanges.pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe((quoteReferenceNumber) => {
        if (formGroup.get('status').value === enumBillStatus.QUOTE && quoteReferenceNumber) {
          formGroup.get('displayName').patchValue(quoteReferenceNumber);
        }
      });

    formGroup
      .get('status')
      .valueChanges.pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe((status: enumBillStatus) => {
        const quoteReferenceNumber = formGroup.get('quoteReferenceNumber').value;
        const billNumber = formGroup.get('billNumber').value;
        if (status === enumBillStatus.QUOTE && quoteReferenceNumber) {
          formGroup.get('displayName').patchValue(quoteReferenceNumber);
        } else if (billNumber && (billNumber.carrier || billNumber.number)) {
          formGroup.get('displayName').patchValue((billNumber.carrier || '') + (billNumber.number || ''));
        }
        const fieldNames = [
          ['billNumber', 'carrier'],
          ['billNumber', 'number'],
          'shippedOnBoardDate',
          'issueDate',
          'placeOfIssue',
          'freightPaymentPlace',
          'freightPayableTotalAmount',
          'receiptPlace',
          'deliveryPlace',
          'finalDestination',
          'incoTermCode',
        ];

        const enableValidation = status !== enumBillStatus.QUOTE;
        this.updateEntityValidation(enableValidation, shipper);
        this.updateEntityValidation(enableValidation, consignee);
        this.updateEntityValidation(enableValidation, notifyParty);
        this.updateEntityValidation(enableValidation, forwardingAgent);
        if (enableValidation) {
          fieldNames.forEach((name) => {
            formGroup.get(name).clearValidators();
            formGroup.get(name).setValidators(Validators.required);
            formGroup.get(name).updateValueAndValidity({ onlySelf: false });
          });
        } else {
          fieldNames.forEach((name) => {
            formGroup.get(name).clearValidators();
            formGroup.get(name).updateValueAndValidity({ onlySelf: false });
          });
        }
      });

    consignee
      .get('entityIdentifierQualifier')
      .valueChanges.pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(() => {
        const status = formGroup.get('status').value;
        const enableValidation = status !== enumBillStatus.QUOTE;
        this.updateEntityValidation(enableValidation, consignee);
      });

    return formGroup;
  }

  public createHouseBillFormGroup() {
    const shipper = this.createLCLEntityModelFormGroup(enumEntityModelCode.SHIPPER, false);
    const consignee = this.createLCLEntityModelFormGroup(enumEntityModelCode.CONSIGNEE);
    const notifyParty = this.createLCLEntityModelFormGroup(enumEntityModelCode.NOTIFY_PARTY, false);
    const forwardingAgent = this.createLCLEntityModelFormGroup(enumEntityModelCode.FORWARDING_AGENT, false);
    const deliveryAgentFormGroup = this.createLCLEntityModelFormGroup(enumEntityModelCode.DELIVERY_AGENT, false);
    consignee
      .get('entityIdentifierQualifier')
      .valueChanges.pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(() => {
        this.updateEntityValidation(true, consignee);
      });

    return new UntypedFormGroup({
      status: new UntypedFormControl(null, Validators.required),
      billNumber: new UntypedFormGroup({
        carrier: new UntypedFormControl(null, Validators.required),
        number: new UntypedFormControl(null, Validators.required),
      }),
      receiptPlace: new UntypedFormControl(null, Validators.required),
      exportReference: new UntypedFormControl(null, Validators.required),
      totalUnitsCount: new UntypedFormControl(null, Validators.required),
      issueDate: new UntypedFormControl(null, Validators.required),
      finalDestination: new UntypedFormControl(null, Validators.required),
      remarks: new UntypedFormControl(null),
      incoTermCode: new UntypedFormControl(null, Validators.required),
      shipper,
      consignee,
      notifyParty,
      forwardingAgent,
      deliveryAgentFormGroup,
    });
  }

  public patchHouseBillByMasterBill(formGroup: UntypedFormGroup, value: ManagementMasterBillModel) {
    if (!value) {
      return;
    }
    const { receiptPlace, finalDestination } = value;
    formGroup.patchValue({
      finalDestination,
      receiptPlace,
    });
  }

  public createPackingListFormGroup() {
    const issuer = this.createLCLEntityModelFormGroup(enumEntityModelCode.ISSUER, false);
    const consignee = this.createLCLEntityModelFormGroup(enumEntityModelCode.CONSIGNEE);
    const notifyParty = this.createLCLEntityModelFormGroup(enumEntityModelCode.NOTIFY_PARTY, false);
    const shipTo = this.createLCLEntityModelFormGroup(enumEntityModelCode.SHIP_TO, false);

    consignee
      .get('entityIdentifierQualifier')
      .valueChanges.pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(() => {
        this.updateEntityValidation(true, consignee);
      });

    return new UntypedFormGroup({
      invoiceNumber: new UntypedFormControl(null, Validators.required),
      printDate: new UntypedFormControl(null, Validators.required),
      departureDate: new UntypedFormControl(null, Validators.required),
      arrivalDate: new UntypedFormControl(null, Validators.required),
      departureNumber: new UntypedFormControl(null, Validators.required),
      issuer,
      consignee,
      notifyParty,
      shipTo,
    });
  }

  public createPackingListItemFormGroup() {
    return new UntypedFormGroup({
      orderNumber: new UntypedFormControl(null, Validators.required),
      licencePlateNumber: new UntypedFormControl(null, Validators.required),
      description: new UntypedFormControl(null),
      width: new UntypedFormControl(null, Validators.required),
      height: new UntypedFormControl(null, Validators.required),
      length: new UntypedFormControl(null, Validators.required),
      volume: new UntypedFormControl(null, Validators.required),
      caseCount: new UntypedFormControl(null, Validators.required),
      totalCasesCount: new UntypedFormControl(null, Validators.required),
      totalUnitsCount: new UntypedFormControl(null, Validators.required),
      netWeight: new UntypedFormControl(null, Validators.required),
      grossWeight: new UntypedFormControl(null, Validators.required),
    });
  }

  public focusFirstInvalidControl(formGroup: UntypedFormGroup, fieldTypeConfig: IFieldTypeConfig[], count: number = 0) {
    if (this.focusSubscription) {
      this.focusSubscription.unsubscribe();
    }
    if (formGroup.invalid) {
      for (const errorFieldConfig of fieldTypeConfig) {
        if (
          formGroup.get(errorFieldConfig.controlName) &&
          formGroup.get(errorFieldConfig.controlName).invalid &&
          formGroup.get(errorFieldConfig.controlName).enabled
        ) {
          let element: HTMLElement;
          if (Array.isArray(errorFieldConfig.controlId)) {
            for (const controlId of errorFieldConfig.controlId) {
              element = document.getElementById(controlId);
              if (element) {
                break;
              }
            }
          } else {
            element = document.getElementById(errorFieldConfig.controlId);
          }
          if (!element) {
            if (count < 4) {
              this.focusSubscription = timer(500)
                .pipe(takeUntil(this.destroy$))
                .subscribe(() => {
                  this.focusFirstInvalidControl(formGroup, fieldTypeConfig, count + 1);
                });
            }
            return;
          }
          if (
            [enumFormControlType.INPUT, enumFormControlType.CHECKBOX_INPUT, enumFormControlType.DATE_PICKER].includes(
              errorFieldConfig.type,
            ) &&
            'focus' in element
          ) {
            element.focus();
          }
          if ([enumFormControlType.SELECT].includes(errorFieldConfig.type)) {
            const input = this.findInput(element);
            if (input && 'focus' in input) {
              input.focus();
            }
          }

          if ('scrollIntoViewIfNeeded' in element) {
            (element as any).scrollIntoViewIfNeeded();
          } else if ('scrollIntoView' in element) {
            (element as any).scrollIntoView();
          }
          break;
        }
      }
    }
  }

  public checkAddressIdForEntity(formGroup: UntypedFormGroup, entity: EntityModel | IsfV1EntityModel): EntityModel | IsfV1EntityModel {
    const result = cloneDeep(entity);
    if (this.isEmptyFormAll(formGroup.get('address') as UntypedFormGroup)) {
      result.address = null;
      result.addressId = null;
    } else if (result.address) {
      result.address.id = 0;
      result.addressId = 0;
    }
    return result;
  }

  public isEmptyFormAll(formGroup: UntypedFormGroup): boolean {
    const value = omit(formGroup.value, ['id']);

    return every(Object.keys(value), (prop) =>
      UtilsHelper.isObject(value[prop]) ? every(Object.keys(value[prop]), (e) => !value[prop][e]) : !value[prop],
    );
  }

  public isEmptyEntityForm(formGroup: UntypedFormGroup): boolean {
    const value = pick(formGroup.value, [
      'name',
      'number',
      'dateOfBirth',
      'identifier',
      'identifierQualifier',
      'entityIdentifier',
      'entityIdentifierQualifier',
      'address',
    ]);

    return every(Object.keys(value), (prop) =>
      UtilsHelper.isObject(value[prop]) ? every(Object.keys(value[prop]), (e) => !value[prop][e]) : !value[prop],
    );
  }

  /**
   * Recursively replace empty-string values ('') with null in a form-value object.
   * Cleared inputs emit '' which the backend rejects for nullable fields (#13138);
   * untouched optional fields are already null. Returns a new object (non-mutating).
   */
  public emptyStringsToNull<T>(value: T): T {
    if (Array.isArray(value)) {
      return (value as unknown[]).map((item: unknown) => this.emptyStringsToNull(item)) as unknown as T;
    }
    if (value !== null && typeof value === 'object') {
      const result: Record<string, unknown> = {};
      Object.keys(value as Record<string, unknown>).forEach((key: string) => {
        const v: unknown = (value as Record<string, unknown>)[key];
        result[key] = v === '' ? null : this.emptyStringsToNull(v);
      });
      return result as T;
    }
    if (typeof value === 'string') {
      return (value === '' ? null : value) as T;
    }
    return value;
  }

  // excludeKeys (#13269) skips top-level controls whose invalid entries the caller reports itself
  // (e.g. the controllingPersons FormArray, whose flat field names would collide with IOR contact keys)
  public validateAllFields(formGroup: UntypedFormGroup, fields: any = {}, excludeKeys: string[] = []) {
    Object.keys(formGroup.controls).forEach((field) => {
      if (excludeKeys.includes(field)) {
        return;
      }
      const control = formGroup.get(field);
      if (control instanceof UntypedFormControl) {
        control.markAsTouched({ onlySelf: true });
        if (control.invalid) {
          fields[field] = field;
        }
      } else if (control instanceof UntypedFormGroup) {
        this.validateAllFields(control, fields);
      } else if (control instanceof UntypedFormArray) {
        for (const cubControl of (control as UntypedFormArray).controls) {
          this.validateAllFields(cubControl as UntypedFormGroup, fields);
        }
      }
    });
    return fields;
  }

  public getInvalidFieldsByCode(
    isfEntity: EntityModel,
    code: enumEntityModelCode,
    isCheckAddressOverride: boolean = false,
  ): { [key: string]: ValidationMessageModel } {
    let pickFields: string[];
    let objToValidate: any = {};
    const isCheckAddress = [
      enumEntityModelCode.IMPORTER,
      enumEntityModelCode.SHIP_TO,
      enumEntityModelCode.MANUFACTURER,
      enumEntityModelCode.SELLER,
      enumEntityModelCode.CONSIGNEE,
      enumEntityModelCode.BUYER,
    ].includes(code);

    switch (code) {
      case enumEntityModelCode.IMPORTER:
      case enumEntityModelCode.CONSOLIDATOR:
        pickFields = ['name', 'type', 'entityIdentifier', 'entityIdentifierQualifier'];
        if (code === enumEntityModelCode.IMPORTER && isfEntity.entityIdentifierQualifier === enumIdentifierQualifier.ANI) {
          pickFields = pickFields.filter((item: string) => item !== 'type');
        }
        break;

      case enumEntityModelCode.CONSIGNEE:
        pickFields = ['name', 'entityIdentifier', 'entityIdentifierQualifier'];
        break;

      case enumEntityModelCode.BUYER:
        pickFields = ['name', 'entityIdentifier', 'entityIdentifierQualifier'];
        break;

      case enumEntityModelCode.SHIP_TO:
      case enumEntityModelCode.SELLER:
      case enumEntityModelCode.MANUFACTURER:
      case enumEntityModelCode.STAFFING_LOCATION:
        pickFields = ['name'];
        break;

      default:
        pickFields = [];
        break;
    }

    if (pickFields.length) {
      objToValidate = pick(isfEntity, pickFields);
    }

    const invalidFields = Object.keys(objToValidate).filter((key) => [null, undefined, ''].includes(objToValidate[key]));
    const invalidAddressFields =
      isCheckAddress || isCheckAddressOverride ? this.getInvalidIsfEntityAddressByCode(isfEntity.address, code) : [];

    return UtilsHelper.concat<string>(invalidFields, invalidAddressFields).reduce((prev, curr) => {
      const newObj = { ...prev };
      const valMessage: ValidationMessageModel = { severity: enumValidationMessageSeverity.ERROR } as ValidationMessageModel;
      valMessage.text = curr.toUpperCase();
      newObj[curr] = valMessage;
      return newObj;
    }, {});
  }

  public isValidIsfEntity(isfEntity: EntityModel, omitField: string[] = [], isCheckAddress: boolean = true) {
    const { validationMessages = [] } = isfEntity;

    if (!validationMessages) {
      return true;
    }

    const isValid = !Object.keys(validationMessages).length;

    return isCheckAddress ? isValid && this.isValidIsfEntityAddress(isfEntity.address) : isValid;
  }

  public isValidIsfEntityAddress(address: AddressModel): boolean {
    if (!address || !address.validationMessages) {
      return true;
    }
    const { validationMessages } = address;

    return !Object.keys(validationMessages).length;
  }

  public isMissingIsfEntityAddressParts(address: AddressModel): boolean {
    if (!address || !address.validationMessages) {
      return true;
    }
    const { validationMessages } = address;

    const missingMessages: ValidationMessageModel[] = (Object.values(validationMessages) as any).filter((message) =>
      [MISSING_CITY, MISSING_ADDRESS, MISSING_STATE, MISSING_COUNTRY, MISSING_POSTAL_CODE].includes(message.text),
    );

    return !!missingMessages.length;
  }

  public setEntityBackError(formGroup: UntypedFormGroup, isfEntity: EntityModel, errors: { [name: string]: IErrorForm }) {
    const validationMessages: {
      [key: string]: {
        value: string;
        messages: string[];
      };
    } = {};

    Object.keys(isfEntity).forEach((fieldName: string) => {
      const value = isfEntity[fieldName];
      const mapValue = EntityValidationHelper.entityMapValidation;

      validationMessages[fieldName] = {
        value,
        messages: mapValue.has(fieldName) ? mapValue.get(fieldName)(isfEntity) : [],
      };

      const firstError = validationMessages[fieldName].messages[0];

      if (errors[fieldName] && firstError) {
        errors[fieldName]['backError'] = `BACK_ERROR.${firstError}`;

        const control = formGroup.get(fieldName);
        if (!!control) {
          control.setErrors({ backError: true });
          control.markAsTouched();
        }
      }
    });

    if (isfEntity && !!isfEntity['address']) {
      const validationMessageAddress: [{ [key: string]: ValidationMessageModel }] = isfEntity['address'].validationMessages;
      const addressFormGroup = formGroup.get('address') as UntypedFormGroup;
      Object.keys(validationMessageAddress).forEach((key) => {
        const lowerCaseKey = key.substr(0, 1).toLowerCase() + key.substr(1);
        if (errors[lowerCaseKey]) {
          errors[lowerCaseKey]['backError'] = `BACK_ERROR.${validationMessageAddress[key].text}`;
          const control = addressFormGroup.get(lowerCaseKey);
          if (!!control) {
            control.setErrors({ backError: true });
            control.markAsTouched();
          }
        }
      });
    }
  }

  public createEntryShipmentFormGroup(achMethodAllowed: boolean) {
    return this.fb.group({
      vesselName: [null, [Validators.required, Validators.maxLength(this.vesselNameMaxLength)]],
      trip: [null, [Validators.required]],
      portOfEntryCode: [null, [Validators.required]],
      portOfUnladingCode: [null, [Validators.required]],
      arrivalDate: [null, [Validators.required]],
      locationCode: [null, [Validators.required]],
      hasSingleExportData: [false],
      differentPortEntry: [false],
      foreignPortOfLadingId: [null, [Validators.required]],
      exportDate: [null, [Validators.required]],
      stateOfDestination: [null, [Validators.required]],
      type: [enumEntrySummaryType.FORMAL, [Validators.required]],
      submissionType: [null, [Validators.required]],
      dutiesPaymentType: [
        enumEntrySummaryDutiesPaymentType.INSTANT,
        achMethodAllowed ? [Validators.required] : [Validators.required, CustomValidator.entryDutiesPaymentType],
      ],
      payerUnitNumber: [null],
      importerId: [null],
    });
  }

  public createEntryShipmentFormGroupWithData(
    data: {
      trip: string;
      vesselName: string;
      portOfEntryCode: string;
      portOfUnladingCode: string;
      differentPortEntry: boolean;
      arrivalDate: string;
      locationCode: string;
      hasSingleExportData: boolean;
      foreignPortOfLadingId: number;
      exportDate: string;
      stateOfDestination: string;
      type: enumEntrySummaryType;
      submissionType: enumEntrySummarySubmissionType;
      dutiesPaymentType: enumEntrySummaryDutiesPaymentType;
      payerUnitNumber: string;
    },
    isAir: boolean,
    achMethodAllowed: boolean,
  ) {
    return this.fb.group({
      vesselName: [data.vesselName, isAir ? null : [Validators.required, Validators.maxLength(this.vesselNameMaxLength)]],
      trip: [data.trip, [Validators.required]],
      portOfEntryCode: [data.portOfEntryCode, [Validators.required]],
      portOfUnladingCode: [data.portOfUnladingCode, [Validators.required]],
      arrivalDate: [data.arrivalDate, [Validators.required]],
      locationCode: [data.locationCode, [Validators.required]],
      hasSingleExportData: [data.hasSingleExportData],
      differentPortEntry: [data.differentPortEntry],
      foreignPortOfLadingId: [data.foreignPortOfLadingId, [Validators.required]],
      exportDate: [data.exportDate, [Validators.required]],
      stateOfDestination: [data.stateOfDestination, [Validators.required]],
      type: [data.type, [Validators.required]],
      submissionType: [data.submissionType, [Validators.required]],
      dutiesPaymentType: [
        data.dutiesPaymentType,
        achMethodAllowed ? [Validators.required] : [Validators.required, CustomValidator.entryDutiesPaymentType],
      ],
      payerUnitNumber: [data.payerUnitNumber],
    });
  }

  /**
   * #13261/#13422: mode-dependent validators of the entry shipment form, shared by the details edit sidebar
   * and the OCR quick-fix step. Mirrors the template visibility: Vessel — Ocean only; Trip — all but Truck;
   * Foreign port — hidden for Air and Truck; Location of goods — optional for Truck.
   */
  public applyEntryShipmentModeValidators(formGroup: UntypedFormGroup, mode: enumTransportationMode): void {
    const isOcean: boolean = mode === enumTransportationMode.OCEAN;
    const isAir: boolean = mode === enumTransportationMode.AIR;
    const isTruck: boolean = mode === enumTransportationMode.TRUCK;
    const modeValidators: { [controlName: string]: ValidatorFn[] | null } = {
      vesselName: isOcean ? [Validators.required, Validators.maxLength(this.vesselNameMaxLength)] : null,
      trip: isTruck ? null : [Validators.required],
      foreignPortOfLadingId: isAir || isTruck ? null : [Validators.required],
      locationCode: isTruck ? null : [Validators.required],
    };
    Object.keys(modeValidators).forEach((controlName: string) => {
      const control: AbstractControl = formGroup.get(controlName);
      control.setValidators(modeValidators[controlName]);
      control.updateValueAndValidity({ emitEvent: false });
    });
  }

  public createCompanyFormGroup(isUnitedStates?: boolean) {
    return {
      id: new UntypedFormControl(null),
      businessName: new UntypedFormControl(null, [Validators.required]),
      identifier: new UntypedFormControl(null, isUnitedStates ? [Validators.required] : []),
      identifierQualifier: new UntypedFormControl(
        isUnitedStates ? enumIdentifierQualifier.EIN : null,
        isUnitedStates ? [Validators.required] : [],
      ),
      dateOfBirth: new UntypedFormControl(null, [CustomValidator.requiredDateOfBirthIfSNN()]),
      businessType: new UntypedFormControl(null, isUnitedStates ? [Validators.required] : []),
      // #13188 — State of Incorporation; required validator applied dynamically in edit-company
      stateOfIncorporation: new UntypedFormControl(null),
      // #13269 — required validator applied dynamically in edit-company (Partnership only)
      partnershipType: new UntypedFormControl(null),
      // #13269 — controlling persons; sections are seeded dynamically in edit-company per business type
      controllingPersons: new UntypedFormArray([]),
      logoUri: new UntypedFormControl(null, []),
      address: this.createGenericManagementAddressFormGroup(),
      defaultBondReferenceNumber: new UntypedFormControl(null),
      payerUnitNumber: new UntypedFormControl(null, [Validators.minLength(6), Validators.maxLength(6)]),
      authorizationAgreementAccepted: new UntypedFormControl(false),
      achIsProvided: new UntypedFormControl(false), // FE only, used for ACH radio select
      cbpAssignedNumber: new UntypedFormControl(false), // FE only, used for Foreign Importer checkbox select

      isAllowedRegistration: new UntypedFormControl(true),
      valueAddedTaxNumber: new UntypedFormControl(null),
      isUnitedStates: new UntypedFormControl(true),
      shippingRole: new UntypedFormControl(enumShippingRole.IMPORTER),
      isAllowedForCustoms: new UntypedFormControl(false),
      isLockedIdentifier: new UntypedFormControl(false),
      validationDate: new UntypedFormControl(null),
      // IOR Contact Information
      isImporterOfRecord: new UntypedFormControl(false),
      importerOfRecordContactInformation: new UntypedFormGroup({
        firstName: new UntypedFormControl(null, [Validators.required]),
        lastName: new UntypedFormControl(null, [Validators.required]),
        phoneNumber: new UntypedFormControl(null, [Validators.required]),
        emailAddress: new UntypedFormControl(null, [Validators.required, Validators.email]),
      }),
    };
  }

  public createCbpAssignedNumberRequestFormGroup() {
    return this.fb.group({
      identifier: new UntypedFormControl(''),
      identifierQualifier: new UntypedFormControl(''),
      businessType: new UntypedFormControl(enumCompanyBusinessType.CORPORATION),
      // #13233 — State of Incorporation; required validator applied dynamically on the application page
      stateOfIncorporation: new UntypedFormControl(null),
      businessName: new UntypedFormControl('', [Validators.required]),
      address: this.createGenericAddressFormGroup(),
      dateOfBirth: new UntypedFormControl(),
      isImporterOfRecord: new UntypedFormControl(false),
      importerOfRecordContactInformation: new UntypedFormGroup({
        firstName: new UntypedFormControl(null, [Validators.required]),
        lastName: new UntypedFormControl(null, [Validators.required]),
        phoneNumber: new UntypedFormControl(null, [Validators.required]),
        emailAddress: new UntypedFormControl(null, [Validators.required, Validators.email]),
      }),
    });
  }

  public createCompanyBondPurchaseFormGroup() {
    return this.fb.group({
      bondType: new UntypedFormControl(enumBondType.CONTINUOUS, [Validators.required]),
      continuousBondDetails: this.fb.group({
        companyName: new UntypedFormControl(null, [Validators.required]),
        address: new UntypedFormControl(null, [Validators.required]),
        importerNumber: new UntypedFormControl(null, [Validators.required]),
        commodities: new UntypedFormControl(null, [Validators.required]),
        businessType: new UntypedFormControl(null, [Validators.required]),
        mainCountryOfOriginCode: new UntypedFormControl(null, [Validators.required]),
        estimatedImportValue: new UntypedFormControl(null, [Validators.required]),
        estimatedDutyValue: new UntypedFormControl(null, [Validators.required]),
        stateOfIncorporationCode: new UntypedFormControl(null, [Validators.required]),
        areCommoditiesSubjectToACD: new UntypedFormControl(true, [Validators.required]),
        notes: new UntypedFormControl(),
      }),
    });
  }

  public createUserFormGroup() {
    return {
      email: new UntypedFormControl({ value: null, disabled: true }, [Validators.required, Validators.maxLength(70)]),
      firstName: new UntypedFormControl(null, [Validators.required]),
      lastName: new UntypedFormControl(null, [Validators.required]),
      phoneNumber: new UntypedFormControl(null, [Validators.required, Validators.maxLength(15)]),
      timeZone: new UntypedFormControl(LA_TIMEZONE_NAME, [Validators.required]),
      monthlyNewslettersSubscriptionActivated: new UntypedFormControl(true),
      advertisingMessagesSubscriptionActivated: new UntypedFormControl(false),
    };
  }

  public createIsfEntityFormGroup(entityCode: enumEntityModelCode, isDestroyComponent: ReplaySubject<boolean>): UntypedFormGroup {
    const identifierRequired = [enumEntityModelCode.IMPORTER, enumEntityModelCode.CONSIGNEE].includes(entityCode);
    const addressRequired = [
      enumEntityModelCode.SELLER,
      enumEntityModelCode.MANUFACTURER,
      enumEntityModelCode.CONSOLIDATOR,
      enumEntityModelCode.STAFFING_LOCATION,
    ].includes(entityCode);
    const fullAddressRequired = [
      enumEntityModelCode.IMPORTER,
      enumEntityModelCode.CONSIGNEE,
      enumEntityModelCode.BUYER,
      enumEntityModelCode.SHIP_TO,
    ].includes(entityCode);
    const requiredStateForCanadaAndMexico = [enumEntityModelCode.SELLER, enumEntityModelCode.MANUFACTURER].includes(entityCode);
    const formGroup = this.fb.group({
      id: new UntypedFormControl(),
      name: new UntypedFormControl(null, Validators.required),
      code: new UntypedFormControl(),
      dateOfBirth: new UntypedFormControl(),
      type: new UntypedFormControl(),
      entityIdentifier: new UntypedFormControl(null, identifierRequired ? Validators.required : null),
      entityIdentifierQualifier: new UntypedFormControl(null, identifierRequired ? Validators.required : null),
      entityId: new UntypedFormControl(),
      dataSource: new UntypedFormControl(null),
      address: this.fb.group({
        id: new UntypedFormControl(),
        city: new UntypedFormControl(null, fullAddressRequired || addressRequired ? Validators.required : null),
        country: new UntypedFormControl(null, fullAddressRequired || addressRequired ? Validators.required : null),
        postalCode: new UntypedFormControl(
          null,
          fullAddressRequired ? [Validators.required, Validators.maxLength(9)] : Validators.maxLength(9),
        ),
        state: new UntypedFormControl(null, fullAddressRequired ? Validators.required : null),
        line1: new UntypedFormControl(
          null,
          fullAddressRequired || addressRequired ? [Validators.required, Validators.maxLength(32)] : Validators.maxLength(32),
        ),
        line2: new UntypedFormControl(null),
        line3: new UntypedFormControl(null),
      }),
    });
    if (identifierRequired) {
      formGroup
        .get('entityIdentifierQualifier')
        .valueChanges.pipe(distinctUntilChanged(), takeUntil(isDestroyComponent))
        .subscribe((entityIdentifierQualifier: enumIdentifierQualifier) => {
          this.updateValidatorsByEntityIdentifierQual(formGroup, identifierRequired, entityIdentifierQualifier);
        });
    }
    if (requiredStateForCanadaAndMexico && !fullAddressRequired) {
      formGroup
        .get(['address', 'country'])
        .valueChanges.pipe(distinctUntilChanged(), takeUntil(isDestroyComponent))
        .subscribe((country: CountryModel) => {
          formGroup.get(['address', 'state']).clearValidators();
          if (country?.code === CANADA_COUNTRY_CODE || country?.code === MEXICO_COUNTRY_CODE) {
            formGroup.get(['address', 'state']).setValidators(Validators.required);
          }
          formGroup.get(['address', 'state']).updateValueAndValidity();
        });
    }
    return formGroup;
  }

  public createIsfShipmentDetailsFormGroup() {
    return this.fb.group({
      code: new UntypedFormControl(enumIsfShipmentCode.HOUSE, { validators: [Validators.required] }),
      bill: new UntypedFormGroup({
        carrier: new UntypedFormControl(null, {
          validators: [Validators.required, Validators.minLength(4), Validators.maxLength(4)],
        }),
        number: new UntypedFormControl(null, { validators: [Validators.required, Validators.minLength(1)] }),
      }),
      type: new UntypedFormControl(enumIsfShipmentType.FORMAL, { validators: Validators.required }),
    });
  }

  public patchIsfShipmentDetailsFormGroup(formGroup: UntypedFormGroup, isfType: enumIsfShipmentType, shipment: ShipmentModel) {
    formGroup.get('type').patchValue(isfType || enumIsfShipmentType.FORMAL);

    const shipmentCode = shipment?.code || enumIsfShipmentCode.HOUSE;
    if (!!shipment) {
      formGroup.patchValue({
        code: shipmentCode,
        bill:
          shipmentCode === enumIsfShipmentCode.HOUSE
            ? shipment.houseBill || { carrier: undefined, number: undefined }
            : shipment.masterBill || { carrier: undefined, number: undefined },
      });
      return;
    }
  }

  public createEntityModelFormGroup(entityCode?: enumEntityModelCode, postalCodeRequired: boolean = true) {
    return new UntypedFormGroup({
      id: new UntypedFormControl(),
      name: new UntypedFormControl(),
      entityCode: new UntypedFormControl(entityCode),
      dateOfBirth: new UntypedFormControl(),
      entityIdentifier: new UntypedFormControl(),
      entityIdentifierQualifier: new UntypedFormControl(null),
      entityId: new UntypedFormControl(),
      address: this.fb.group(this.createIsfAddress(postalCodeRequired)),
      email: new UntypedFormControl(null, [Validators.maxLength(70), Validators.pattern(emailPattern)]),
      phone: new UntypedFormControl(null, [Validators.maxLength(15)]),
      fax: new UntypedFormControl(null, [Validators.maxLength(15)]),
    });
  }

  public createLCLEntityModelFormGroup(entityCode: enumEntityModelCode, isIdentifierRequired = true) {
    const entityIdentifierValidators = isIdentifierRequired ? [Validators.required] : null;

    return new UntypedFormGroup({
      id: new UntypedFormControl(),
      name: new UntypedFormControl(null, Validators.required),
      entityCode: new UntypedFormControl(entityCode),
      dateOfBirth: new UntypedFormControl(),
      entityIdentifier: new UntypedFormControl(null, entityIdentifierValidators),
      entityIdentifierQualifier: new UntypedFormControl(null, entityIdentifierValidators),
      address: this.fb.group(this.createLCLEntityAddress()),
      email: new UntypedFormControl(null, [Validators.required, Validators.maxLength(70), Validators.pattern(emailPattern)]),
      phone: new UntypedFormControl(null, [Validators.required, Validators.maxLength(15)]),
      fax: new UntypedFormControl(null, [Validators.maxLength(15)]),
    });
  }

  public createLCLEntityAddress() {
    return {
      city: new UntypedFormControl(null),
      country: new UntypedFormControl(null),
      postalCode: new UntypedFormControl(null, [Validators.maxLength(9)]),
      state: new UntypedFormControl(null),
      line1: new UntypedFormControl(null),
      line2: new UntypedFormControl(null),
      line3: new UntypedFormControl(null),
    };
  }

  public updateEntityValidation(required: boolean, formGroup: UntypedFormGroup | AbstractControl) {
    const entityCode: enumEntityModelCode = formGroup.get('entityCode').value;
    formGroup.get('name').clearValidators();
    formGroup.get(['address', 'city']).clearValidators();
    formGroup.get(['address', 'country']).clearValidators();
    formGroup.get(['address', 'line1']).clearValidators();
    formGroup.get(['address', 'postalCode']).clearValidators();
    formGroup.get('entityIdentifier').clearValidators();
    formGroup.get('entityIdentifierQualifier').clearValidators();

    const entityIdentifierQualifier = formGroup.get('entityIdentifierQualifier').value;
    const entityIdentifierValidator = this.getIdentifierValidator(entityIdentifierQualifier);
    const entityIdentifierValidators = [];
    if (entityIdentifierValidator) {
      entityIdentifierValidators.push(entityIdentifierValidator());
    }

    if (required) {
      formGroup.get('name').setValidators(Validators.required);
      formGroup.get(['address', 'city']).setValidators(Validators.required);
      formGroup.get(['address', 'country']).setValidators(Validators.required);
      formGroup.get(['address', 'line1']).setValidators(Validators.required);
      if (entityCode === enumEntityModelCode.CONSIGNEE) {
        formGroup.get('entityIdentifierQualifier').setValidators(Validators.required);
        entityIdentifierValidators.push(Validators.required);
      }
      if (
        [enumEntityModelCode.IMPORTER, enumEntityModelCode.CONSIGNEE, enumEntityModelCode.BUYER, enumEntityModelCode.SHIP_TO].includes(
          entityCode,
        )
      ) {
        formGroup.get(['address', 'postalCode']).setValidators([Validators.required, Validators.maxLength(9)]);
      }
    }
    if (entityIdentifierValidators && entityIdentifierValidators.length) {
      formGroup.get('entityIdentifier').setValidators(entityIdentifierValidators);
    }

    formGroup.get('name').updateValueAndValidity();
    formGroup.get(['address', 'city']).updateValueAndValidity();
    formGroup.get(['address', 'country']).updateValueAndValidity();
    formGroup.get(['address', 'line1']).updateValueAndValidity();
    formGroup.get(['address', 'postalCode']).updateValueAndValidity();
    formGroup.get('entityIdentifier').updateValueAndValidity();
    formGroup.get('entityIdentifierQualifier').updateValueAndValidity();
  }

  public getIdentifierValidator(valueQual: enumIdentifierQualifier) {
    switch (valueQual) {
      case enumIdentifierQualifier.EIN:
        return identifierEINValidator;
      case enumIdentifierQualifier.SSN:
        return identifierSSNValidator;
      default:
        return null;
    }
  }

  public createDeliveryOrderForm() {
    const fg = new UntypedFormGroup({
      id: new UntypedFormControl(0),
      type: new UntypedFormControl(null, Validators.required),
      trucker: this.createEntityModelFormGroup(null, false),
      truckerId: new UntypedFormControl(0),
      shipTo: this.createEntityModelFormGroup(),
      shipToId: new UntypedFormControl(0),
      parts: new UntypedFormArray([this.createDeliveryOrderPart()]),
      description: new UntypedFormControl(),
      shipmentId: new UntypedFormControl(null, Validators.required),
      pickupTimeFrom: new UntypedFormControl(null, Validators.required),
      pickupTimeTo: new UntypedFormControl(null),
      pickupLocation: new UntypedFormGroup({
        code: new UntypedFormControl(null),
        name: new UntypedFormControl(null, Validators.required),
        stateCode: new UntypedFormControl(null, Validators.required),
        city: new UntypedFormControl(null, Validators.required),
        address: new UntypedFormControl(null, Validators.required),
      }),
    });

    fg.get('trucker')
      .get('phone')
      .setValidators([Validators.required, Validators.maxLength(15)]);
    fg.get('trucker')
      .get('email')
      .setValidators([Validators.required, Validators.maxLength(70), Validators.pattern(emailPattern)]);
    fg.get('trucker').get('name').setValidators(Validators.required);

    fg.get('shipTo')
      .get('phone')
      .setValidators([Validators.required, Validators.maxLength(15)]);
    fg.get('shipTo')
      .get('email')
      .setValidators([Validators.required, Validators.maxLength(70), Validators.pattern(emailPattern)]);
    return fg;
  }

  public markAsTouched(formToCheck: AbstractControl) {
    if (formToCheck) {
      let fg: UntypedFormGroup;
      let fc: UntypedFormControl;
      let fa: UntypedFormArray;
      switch (true) {
        case formToCheck instanceof UntypedFormGroup:
          fg = formToCheck as UntypedFormGroup;
          break;

        case formToCheck instanceof UntypedFormControl:
          fc = formToCheck as UntypedFormControl;
          break;

        case formToCheck instanceof UntypedFormArray:
          fa = formToCheck as UntypedFormArray;
          break;

        default:
          break;
      }
      if (fa) {
        for (const control of fa.controls) {
          this.markAsTouched(control);
        }
      }
      if (fg) {
        for (const controlName in fg.controls) {
          if (controlName) {
            this.markAsTouched(fg.get(controlName));
          }
        }
      }

      if (fc && fc.enabled) {
        fc.markAsTouched();
      }
    }
  }

  public updateValueAndValidity(formToUpdate: AbstractControl) {
    if (formToUpdate) {
      let fg: UntypedFormGroup;
      let fc: UntypedFormControl;
      let fa: UntypedFormArray;
      switch (true) {
        case formToUpdate instanceof UntypedFormGroup:
          fg = formToUpdate as UntypedFormGroup;
          break;

        case formToUpdate instanceof UntypedFormControl:
          fc = formToUpdate as UntypedFormControl;
          break;

        case formToUpdate instanceof UntypedFormArray:
          fa = formToUpdate as UntypedFormArray;
          break;

        default:
          break;
      }
      if (fa) {
        for (const control of fa.controls) {
          this.updateValueAndValidity(control);
        }
      }
      if (fg) {
        for (const controlName in fg.controls) {
          if (controlName) {
            this.updateValueAndValidity(fg.get(controlName));
          }
        }
      }

      if (fc && fc.enabled) {
        fc.updateValueAndValidity();
      }
    }
  }

  public createDeliveryOrderPart() {
    return new UntypedFormGroup({
      id: new UntypedFormControl(0),
      quantity: new UntypedFormControl(),
      unit: new UntypedFormControl(),
      partId: new UntypedFormControl(),
      containerNumber: new UntypedFormControl(),
      description: new UntypedFormControl(),
      marks: new UntypedFormControl(),
      weight: new UntypedFormControl(),
      weightUnit: new UntypedFormControl(enumWeightUnit.KG),
      measurements: new UntypedFormControl(),
      isEdit: new UntypedFormControl(true),
    });
  }

  public patchIsfEntity(
    formGroup: UntypedFormGroup,
    entity: IsfV1EntityModel | EntityModel,
    options?: { onlySelf?: boolean; emitEvent?: boolean },
  ) {
    let patchValue;
    if (!entity || !entity.address) {
      formGroup.get('address').reset();
      patchValue = omit(entity, ['address']);
    } else {
      patchValue = entity;
    }
    formGroup.patchValue(pick(patchValue, Object.keys(formGroup.value)), options);
  }

  public patchAddress(
    addressFormGroup: UntypedFormGroup,
    addressValue: any,
    updateCountry: boolean = false,
    options?: { onlySelf?: boolean; emitEvent?: boolean },
  ) {
    const valueToPatch = pick(addressValue, Object.keys(addressFormGroup.value));
    const countryForm = addressFormGroup.get('country');
    const stateForm = addressFormGroup.get('state');

    if (valueToPatch) {
      const countryValue = valueToPatch.country;
      const stateValue = valueToPatch.state;

      if (countryForm instanceof UntypedFormGroup && !countryValue) {
        valueToPatch.country = updateCountry ? this.setNullForEveryField(countryForm.value) : countryForm.value;
      }

      if (stateForm instanceof UntypedFormGroup && !stateValue) {
        valueToPatch.state = this.setNullForEveryField(stateForm.value);
      }
    }

    addressFormGroup.patchValue(valueToPatch);
  }

  public patchDelivery(fg: UntypedFormGroup, delivery: DeliveryOrderModel) {
    const valuesToUpdate = cloneDeep(delivery);
    const parts = cloneDeep(delivery.parts);

    this.patchIsfEntity(fg.get('shipTo') as UntypedFormGroup, valuesToUpdate.shipTo);
    delete valuesToUpdate.shipTo;

    this.patchIsfEntity(fg.get('trucker') as UntypedFormGroup, valuesToUpdate.trucker);
    delete valuesToUpdate.trucker;

    fg.get('shipmentId').patchValue(valuesToUpdate.shipmentId, { emitValue: false });
    delete valuesToUpdate.shipmentId;

    delete valuesToUpdate.parts;
    const partsForm = fg.get('parts') as UntypedFormArray;
    if (partsForm.controls.length > parts.length) {
      while (partsForm.controls.length > parts.length) {
        partsForm.removeAt(partsForm.controls.length - 1);
      }
    }

    if (partsForm.controls.length < parts.length) {
      while (partsForm.controls.length < parts.length) {
        partsForm.push(this.createDeliveryOrderPart());
      }
    }

    partsForm.controls.forEach((control, index) => {
      control.patchValue(this.prepareValue(parts[index], control.value));
      if (delivery.type === enumDeliveryOrderType.PARTIAL) {
        control.get('isEdit').patchValue(false);
      }
    });

    const { pickupTimeFrom, pickupTimeTo } = valuesToUpdate;

    valuesToUpdate.pickupTimeFrom = this.dateTimezoneService.getUserTimezoneDate(pickupTimeFrom);
    valuesToUpdate.pickupTimeTo = this.dateTimezoneService.getUserTimezoneDate(pickupTimeTo);

    fg.patchValue(this.prepareValue(valuesToUpdate, fg.value));
  }

  public prePatchDeliveryByDelivery(fg: UntypedFormGroup, delivery: DeliveryOrderModel) {
    const { shipTo, trucker } = cloneDeep(delivery);

    this.patchIsfEntity(fg.get('shipTo') as UntypedFormGroup, omit(shipTo, ['id']) as EntityModel);
    this.patchIsfEntity(fg.get('trucker') as UntypedFormGroup, omit(trucker, ['id']) as EntityModel);
  }

  public prePatchDeliveryByShipment(fg: UntypedFormGroup, shipment: ShipmentModel, importer: EntityModel) {
    const { shipTo } = cloneDeep(shipment);

    if (shipTo) {
      this.patchIsfEntity(fg.get('shipTo') as UntypedFormGroup, omit(shipTo, ['id']) as EntityModel);
    } else if (importer) {
      this.patchIsfEntity(fg.get('shipTo') as UntypedFormGroup, omit(importer, ['id']) as EntityModel);
    }
  }

  public createErrorsForIsfFormGroup(): { [name: string]: IErrorForm } {
    return {
      identifier: {
        required: 'FORM_ERROR.REQUIRED',
        backError: '',
        notMatchEIN: 'FORM_ERROR.NOT_MATCH_EIN',
        wrongFormatIEN: 'FORM_ERROR.WRONG_FORMAT_EIN',
        notMatchSSN: 'FORM_ERROR.NOT_MATCH_SSN',
      },
      entityIdentifier: {
        required: 'FORM_ERROR.REQUIRED',
        backError: '',
        notMatchEIN: 'FORM_ERROR.NOT_MATCH_EIN',
        wrongFormatIEN: 'FORM_ERROR.WRONG_FORMAT_EIN',
        notMatchSSN: 'FORM_ERROR.NOT_MATCH_SSN',
      },
      entityIdentifierQualifier: {
        required: 'FORM_ERROR.REQUIRED',
      },
      identifierQualifier: {
        required: 'FORM_ERROR.REQUIRED',
      },
      dateOfBirth: {
        required: 'FORM_ERROR.REQUIRED',
        minlength: 'FORM_ERROR.MIN_LENGTH',
        maxlength: 'FORM_ERROR.MAX_LENGTH',
      },
      address: { required: 'FORM_ERROR.REQUIRED' },
      name: { required: 'FORM_ERROR.REQUIRED' },
      city: { required: 'FORM_ERROR.REQUIRED' },
      country: { required: 'FORM_ERROR.REQUIRED' },
      state: { required: 'FORM_ERROR.REQUIRED' },
      postalCode: { required: 'FORM_ERROR.REQUIRED' },
      hts: { required: 'FORM_ERROR.REQUIRED' },
      fax: { required: 'FORM_ERROR.REQUIRED', maxlength: 'FORM_ERROR.MAX_LENGTH', validatePhoneNumber: 'FORM_ERROR.PHONE_NUMBER' },
      phone: { required: 'FORM_ERROR.REQUIRED', maxlength: 'FORM_ERROR.MAX_LENGTH', validatePhoneNumber: 'FORM_ERROR.PHONE_NUMBER' },
      email: { required: 'FORM_ERROR.REQUIRED', maxLength: 'FORM_ERROR.MAX_LENGTH', pattern: 'FORM_ERROR.LOGIN_EMAIL' },
    };
  }

  public createAddressForm(address?: AddressModel): UntypedFormGroup {
    return this.fb.group({
      id: !address ? null : address.id,
      city: !address ? '' : address.city,
      postalCode: !address ? '' : address.postalCode,
      line1: !address ? '' : address?.line1,
      line2: !address ? '' : address?.line2,
      line3: !address ? '' : address?.line3,
      state: this.fb.group({
        id: !address || !address.state ? 0 : address.state.id,
        code: !address || !address.state ? '' : address.state.code,
        description: !address || !address.state ? '' : address.state.description,
        countryCode: !address || !address.state ? '' : address.state.countryCode,
        unifiedCode: !address || !address.state ? '' : address.state.unifiedCode,
      }),
      country: this.fb.group({
        id: !address || !address.country ? 0 : address.country.id,
        code: !address || !address.country ? '' : address.country.code,
        description: !address || !address.country ? '' : address.country.description,
      }),
    });
  }

  public createEntrySummaryBondForm(): UntypedFormGroup {
    return this.fb.group({
      type: new UntypedFormControl(enumBondType.SINGLE_TRANSACTION, [Validators.required]),
      suretyCode: new UntypedFormControl(null, [Validators.required, suretyCodeValidator()]),
      referenceNumber: new UntypedFormControl(null, [referenceNumberValidator(true)]),
      bondAmount: new UntypedFormControl(50000, [Validators.required]),
    });
  }

  public createIsfBondForm(isDestroyComponent: ReplaySubject<boolean>, updateOn: 'change' | 'blur' | 'submit' = 'blur'): UntypedFormGroup {
    const formGroup = this.fb.group({
      type: new UntypedFormControl(null, Validators.required),
      suretyCode: new UntypedFormControl(null, { validators: [Validators.required, suretyCodeValidator()], updateOn }),
      referenceNumber: new UntypedFormControl(),
      bondAmount: new UntypedFormControl(null, { validators: [Validators.required], updateOn }),
    });
    formGroup
      .get('type')
      .valueChanges.pipe(takeUntil(isDestroyComponent))
      .subscribe((type: enumBondType) => {
        formGroup.get('suretyCode').clearValidators();
        if (type === enumBondType.SINGLE_TRANSACTION) {
          formGroup.get('suretyCode').setValidators([Validators.required, suretyCodeValidator()]);
        }
        formGroup.get('suretyCode').updateValueAndValidity();
      });
    return formGroup;
  }

  public createPurchaseBondForm(): UntypedFormGroup {
    return this.fb.group({
      portCode: new UntypedFormControl(null, Validators.required),
    });
  }

  public createLineItemForm(): UntypedFormGroup {
    return this.fb.group({
      htsNumber: new UntypedFormControl(null),
      countryOfOrigin: new UntypedFormControl(null),
      countryOfOriginId: new UntypedFormControl(null, Validators.required),
      stateOfOrigin: new UntypedFormControl(null),
      countryOfExport: new UntypedFormControl(null),
      countryOfExportId: new UntypedFormControl(null, Validators.required),
      description: new UntypedFormControl(null, [Validators.maxLength(LINE_ITEM_DESCRIPTION_MAX_LENGTH), Validators.required]),
      grossWeight: new UntypedFormControl(null, Validators.required),
      value: new UntypedFormControl(null, [Validators.required]),
      units: new UntypedFormArray([]),
      hasNaftaNetCostIndicator: new UntypedFormControl(false),
      specialChapterHtsNumber: new UntypedFormControl(null),
    });
  }

  public createISFOrESStateForm(): UntypedFormGroup {
    return this.fb.group({
      state: new UntypedFormControl(null, Validators.required),
      customState: new UntypedFormGroup({
        header: new UntypedFormControl(),
        description: new UntypedFormControl(),
        internalNotes: new UntypedFormControl(),
      }),
    });
  }

  public createManagementVoyageForm(voyage: ManagementVoyageModel): UntypedFormGroup {
    return this.fb.group({
      displayName: [voyage.displayName, Validators.required],
      vesselName: [voyage.vesselName],
      voyageNumber: [voyage.voyageNumber],
      estimatedDepartureTime: [voyage.estimatedDepartureTime, Validators.required],
      estimatedArrivalTime: [voyage.estimatedArrivalTime, Validators.required],
      actualDepartureTime: [voyage.actualDepartureTime, Validators.required],
      actualArrivalTime: [voyage.actualArrivalTime, Validators.required],
      warehouseCutoff: [voyage.warehouseCutoff, Validators.required],
      status: [voyage.status, Validators.required],
      combinedPricelistId: [voyage.combinedPricelist?.id, Validators.required],
    });
  }

  public updateValidatorsByEntityIdentifierQual(
    formGroup: UntypedFormGroup,
    identifierRequired: boolean,
    entityIdentifierQualifier: enumIdentifierQualifier,
    options?: { onlySelf?: boolean; emitEvent?: boolean },
  ): void {
    formGroup.get('entityIdentifier').clearValidators();
    formGroup.get('dateOfBirth').clearValidators();
    const identifierValidators = identifierRequired ? [Validators.required] : [];
    const identifierValidator = this.getIdentifierValidator(entityIdentifierQualifier);
    if (identifierValidator) {
      identifierValidators.push(identifierValidator());
    }

    if (identifierValidators.length) {
      formGroup.get('entityIdentifier').setValidators(identifierValidators);
      formGroup.get('entityIdentifier').updateValueAndValidity(options);
    }
    if (entityIdentifierQualifier === enumIdentifierQualifier.SSN) {
      formGroup.get('dateOfBirth').setValidators(Validators.required);
    }
    formGroup.get('dateOfBirth').updateValueAndValidity(options);
  }

  private setNullForEveryField(object: any) {
    const result = cloneDeep(object);
    for (const field in result) {
      if (result.hasOwnProperty(field)) {
        result[field] = null;
      }
    }
    return result;
  }

  private getInvalidIsfEntityAddressByCode(address: AddressModel, code: enumEntityModelCode) {
    let pickFields: string[];
    let objToValidate: any = {};

    switch (code) {
      case enumEntityModelCode.IMPORTER:
        pickFields = ['city', 'country', 'postalCode', 'state', 'line1'];
        if (!!address?.country?.code && ![USA_COUNTRY_CODE, CANADA_COUNTRY_CODE, MEXICO_COUNTRY_CODE].includes(address.country.code)) {
          pickFields = pickFields.filter((item: string) => item !== 'state');
        }
        break;

      case enumEntityModelCode.CONSIGNEE:
      case enumEntityModelCode.SHIP_TO:
        pickFields = ['city', 'country', 'postalCode', 'state', 'line1'];
        break;

      case enumEntityModelCode.BUYER:
        pickFields = ['city', 'postalCode', 'state', 'line1'];
        break;

      case enumEntityModelCode.CONSOLIDATOR:
      case enumEntityModelCode.STAFFING_LOCATION:
        pickFields = ['city', 'country', 'state', 'line1'];
        break;

      case enumEntityModelCode.SELLER:
      case enumEntityModelCode.MANUFACTURER:
        pickFields = ['city', 'country', 'line1'];
        if (!!address?.country?.code && [USA_COUNTRY_CODE, CANADA_COUNTRY_CODE, MEXICO_COUNTRY_CODE].includes(address.country.code)) {
          pickFields.push('state');
        }
        break;

      default:
        pickFields = [];
        break;
    }

    if (pickFields.length) {
      objToValidate = pick(address, pickFields);
    }

    return Object.keys(objToValidate).filter((key) => [null, undefined, ''].includes(objToValidate[key]));
  }

  private createIsfAddress(postalCodeRequired: boolean = true) {
    const postalCodeValidators: ValidatorFn[] = [Validators.maxLength(9)];

    if (postalCodeRequired) {
      postalCodeValidators.push(Validators.required);
    }

    return {
      id: new UntypedFormControl(),
      city: new UntypedFormControl(),
      country: new UntypedFormControl(),
      postalCode: new UntypedFormControl(null, postalCodeValidators),
      state: new UntypedFormControl(),
      line1: new UntypedFormControl(),
      line2: new UntypedFormControl(),
      line3: new UntypedFormControl(),
    };
  }

  private prepareValue(value: any, formValue: any) {
    for (const field in formValue) {
      if (field && UtilsHelper.isObject(formValue[field]) && value[field]) {
        value[field] = this.prepareValue(value[field], formValue[field]);
      }
    }
    return pick(value, Object.keys(formValue));
  }

  private findInput(element: HTMLElement | Element): HTMLInputElement {
    for (const child of Array.from(element.children)) {
      if (child.nodeName === 'INPUT') {
        return child as HTMLInputElement;
      } else {
        const deepChildInput = this.findInput(child);
        if (deepChildInput) {
          return deepChildInput;
        }
      }
    }

    return undefined;
  }
}
